<template>
  <ImageLayoutSettings v-if="product === PRODUCT_TYPE.FORM"></ImageLayoutSettings>
  <FormLayoutType v-if="product === PRODUCT_TYPE.FORM"></FormLayoutType>
  <SurveyLayoutType v-if="product === PRODUCT_TYPE.SURVEY"></SurveyLayoutType>
  <!-- Survey Default Image Settings -->
  <div v-if="product === PRODUCT_TYPE.SURVEY" class="mt-4 w-full">
    <ImageLayoutSettings
      :show-image="store.surveyImageSettings.surveyDefault.showImage"
      :desktop-image-layout="store.surveyImageSettings.surveyDefault.desktopImageLayout"
      :mobile-image-layout="store.surveyImageSettings.surveyDefault.mobileImageLayout"
      :header-image-src="store.surveyImageSettings.surveyDefault.headerImageSrc"
      :mobile-header-image-src="store.surveyImageSettings.surveyDefault.mobileHeaderImageSrc"
      :is-survey="true"
      :selected-slide-index="0"
      :is-editing-survey-default="true"
      @update:show-image="updateSurveyDefaultShowImage"
      @update:desktop-image-layout="updateSurveyDefaultDesktopLayout"
      @update:mobile-image-layout="updateSurveyDefaultMobileLayout"
      @update:header-image-src="updateSurveyDefaultHeaderImage"
      @update:mobile-header-image-src="updateSurveyDefaultMobileHeaderImage"
      @update:image-field-spacing="updateSurveyDefaultImageFieldSpacing"
      @update:image-corner-radius="updateSurveyDefaultImageCornerRadius"
    />
  </div>
  <InputStyleType
    :label="t('common.inputStyle')"
    :value="store?.builderConfig.inputStyleType"
    @update:value="handleInputStyleType"
  />
  <div class="grid grid-cols-2 gap-4">
    <PixelNumber
      :label="t('common.width')"
      :value="parseFloat(store?.formStyle?.width)"
      :max="1800"
      :icon="SpacingWidth01Icon"
      @update:value="handleWidth"
    />
    <PixelNumber
      :label="t('common.style.fieldSpacing')"
      :value="parseFloat(store?.formStyle.fieldSpacing)"
      :max="1800"
      @update:value="handleFieldSpacing"
    />
    <PixelNumber
      :label="t('common.style.labelAlignment.labelWidth')"
      :value="parseFloat(store?.fieldStyle?.labelWidth)"
      :max="1800"
      @update:value="handleLabelWidth"
    />
    <LabelAlignment
      :label-alignment="store.fieldStyle.labelAlignment"
      @update:label-alignment="handleLabelAlignment"
    ></LabelAlignment>
  </div>
  <PaddingInput
    :label="t('common.marginPadding')"
    :padding="currentPadding"
    :margin="currentMargin"
    :enable-margin-top-bottom="true"
    :show-device-mode="true"
    @update-padding="handleFormPadding"
    @update-margin="handleFormMargin"
  ></PaddingInput>
  <Switch
    :label="t('common.showLabel')"
    :value="store?.builderConfig.formLabelVisible"
    @update:value="handleLabelVisible"
  />

  <Switch
    v-if="product === PRODUCT_TYPE.FORM && (store.builder.version <= 4 || !store.builder.version)"
    :value="store.builderConfig.fullScreenMode"
    :label="t('form.fullScreenMode')"
    :tooltip="true"
    :tooltip-message="tooltipMessage.fullScreenMode"
    @update:value="handleFullScreenMode"
  />
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE, tooltipMessage } from "@/util/constants";
import { getBorderBasedOnCurrentSetup } from "@/util/default";
import { AlignmentPosition } from "@/util/methods";
import { SpacingWidth01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import InputStyleType from "../Elements/InputStyleType.vue";
import LabelAlignment from "../Elements/LabelAlignment.vue";
import PaddingInput from "../Elements/PaddingInput.vue";
import PixelNumber from "../Elements/PixelNumber.vue";
import Switch from "../Elements/Switch.vue";
import FormLayoutType from "../StyleSettings/FormLayoutType.vue";
import ImageLayoutSettings from "../StyleSettings/ImageLayoutSettings.vue";
import SurveyLayoutType from "../StyleSettings/SurveyLayoutType.vue";
const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  product: {
    type: String,
    default: PRODUCT_TYPE.FORM,
  },
});

// Initialize extra padding computation during component mount
const initializeExtraPadding = () => {
  // Initialize desktop padding if it doesn't exist
  if (!store.formStyle.padding) {
    store.formStyle.padding = { top: 0, right: 0, bottom: 0, left: 0 };
  }

  // Initialize mobile padding if it doesn't exist
  if (!store.formStyle.mobilePadding) {
    store.formStyle.mobilePadding = { ...store.formStyle.padding };
  }

  // Add extra padding only if not already processed
  if (!store.formStyle.mobilePadding.extraPaddingComputingProcessed) {
    store.formStyle.mobilePadding.top += 30;
    store.formStyle.mobilePadding.bottom += 30;
    store.formStyle.mobilePadding.extraPaddingComputingProcessed = true;
    store.anyUnsavedChanges = true;
  }

  if (!store.formStyle.padding.extraPaddingComputingProcessed) {
    store.formStyle.padding.top += 30;
    store.formStyle.padding.bottom += 30;
    store.formStyle.padding.extraPaddingComputingProcessed = true;
    store.anyUnsavedChanges = true;
  }
};

onMounted(() => {
  initializeExtraPadding();
});

const handleWidth = (val: number) => {
  store.formStyle.width = val;
  store.anyUnsavedChanges = true;
};

const handleInputStyleType = (val: string) => {
  if (val === "line") {
    //For the first time
    if (store.fieldStyle.border.type === "none") {
      store.fieldStyle.bgColor = "FFFFFF";
    }
    //Every time user change input style left padding will be zero
    store.fieldStyle.padding.left = 0;
    //PreStyle based on change to line type
    const { borderSize, borderType, borderColor } = getBorderBasedOnCurrentSetup(
      store.fieldStyle.border,
      store.formStyle.background
    );
    store.fieldStyle.border.border = borderSize;
    store.fieldStyle.border.type = borderType;
    store.fieldStyle.border.color = borderColor;
  }
  if (val === "box") {
    store.fieldStyle.padding.left = 20;
  }

  store.builderConfig.inputStyleType = val;
  store.anyUnsavedChanges = true;
};

const handleLabelVisible = (val: boolean) => {
  store.builderConfig.formLabelVisible = val;
  store.anyUnsavedChanges = true;
};

const handleLabelAlignment = (val: AlignmentPosition) => {
  store.fieldStyle.labelAlignment = val;
  store.applyLabelWidthForSidebarImageLayout(val);
  store.anyUnsavedChanges = true;
};

const handleFullScreenMode = (value: boolean) => {
  store.builderConfig.fullScreenMode = value;
  store.anyUnsavedChanges = true;
};

const handleFormPadding = (side: string, value: number) => {
  if (store.deviceMode === "mobile") {
    if (!store.formStyle.mobilePadding) {
      store.formStyle.mobilePadding = { ...store.formStyle.padding };
    }
    store.formStyle.mobilePadding[side] = value;
  } else {
    store.formStyle.padding[side] = value;

    if (!store.formStyle.mobilePadding) {
      store.formStyle.mobilePadding = { ...store.formStyle.padding };
    }
  }
  store.anyUnsavedChanges = true;
};

const handleFormMargin = (side: string, value: number | "auto") => {
  if (!store.formStyle.margin) {
    store.formStyle.margin = { top: "auto", bottom: "auto", left: "auto", right: "auto" } as any;
  }
  if (store.deviceMode === "mobile") {
    if (!store.formStyle.mobileMargin) {
      store.formStyle.mobileMargin = { ...store.formStyle.margin } as any;
    }
    (store.formStyle.mobileMargin as any)[side] = value;
  } else {
    (store.formStyle.margin as any)[side] = value;
    if (!store.formStyle.mobileMargin) {
      store.formStyle.mobileMargin = { ...store.formStyle.margin } as any;
    }
  }
  store.anyUnsavedChanges = true;
};

const handleLabelWidth = (val: number) => {
  store.fieldStyle.labelWidth = val;
  store.anyUnsavedChanges = true;
};

const handleFieldSpacing = (val: number) => {
  store.formStyle.fieldSpacing = val;
  store.anyUnsavedChanges = true;
};

const currentPadding = computed(() => {
  return store.deviceMode === "mobile"
    ? store.formStyle.mobilePadding || store.formStyle.padding
    : store.formStyle.padding;
});

const currentMargin = computed(() => {
  const base = (store.formStyle.margin || {
    top: "auto",
    bottom: "auto",
    left: "auto",
    right: "auto",
  }) as any;
  const mobile = (store.formStyle.mobileMargin || base) as any;
  return store.deviceMode === "mobile" ? mobile : base;
});
// Survey Default Image Settings Methods
const updateSurveyDefaultShowImage = (value: boolean) => {
  store.surveyImageSettings.surveyDefault.showImage = value;
  store.anyUnsavedChanges = true;
};

const updateSurveyDefaultDesktopLayout = (value: string) => {
  store.surveyImageSettings.surveyDefault.desktopImageLayout = value;
  store.anyUnsavedChanges = true;
};

const updateSurveyDefaultMobileLayout = (value: string) => {
  store.surveyImageSettings.surveyDefault.mobileImageLayout = value;
  store.anyUnsavedChanges = true;
};

const updateSurveyDefaultHeaderImage = (value: string) => {
  store.surveyImageSettings.surveyDefault.headerImageSrc = value;
  store.anyUnsavedChanges = true;
};

const updateSurveyDefaultMobileHeaderImage = (value: string) => {
  store.surveyImageSettings.surveyDefault.mobileHeaderImageSrc = value;
  store.anyUnsavedChanges = true;
};

const updateSurveyDefaultImageFieldSpacing = (value: number) => {
  store.surveyImageSettings.surveyDefault.imageFieldSpacing = value;
  store.anyUnsavedChanges = true;
};

const updateSurveyDefaultImageCornerRadius = (value: number) => {
  store.surveyImageSettings.surveyDefault.imageCornerRadius = value;
  store.anyUnsavedChanges = true;
};
</script>
